import { CUSTOMER_TYPE, PAYMENT_METHOD, RECIPIENT_TYPES, PAYMENT_PROVIDERS } from 'components/common/constants';
import {
  replaceSpaces,
  strToFloat,
  trimTextContent,
  isCryptoProvider,
  isBeneficiaryStateProvider
} from 'services/utils';

// Universal model that works for all provider types and scenarios
export default function CreateNewPaymentFormModel({ providerTableData, data }) {
  // For template creation (when called from ReviewAndSaveNewPaymentFormStep without providerTableData)
  if (!providerTableData && !data.wallet) {
    return createTemplateModel(data);
  }

  // For transaction validation (when called from validateTransfer/validateCryptoTransfer)
  const wallet = data.wallet;
  const transferProvider = wallet?.transfer_provider;
  const paymentMethod = data.paymentMethod;

  // Crypto provider validation
  if (isCryptoProvider(transferProvider)) {
    return createCryptoModel({ providerTableData, data });
  }

  // Clear Junction provider validation
  if (transferProvider === PAYMENT_PROVIDERS.CLEARJUNCTION) {
    return createClearJunctionModel({ providerTableData, data });
  }

  // All other providers (OpenPayd, Manual, etc.)
  const isAdvanced = paymentMethod === PAYMENT_METHOD.SWIFT || paymentMethod === PAYMENT_METHOD.TARGET2;
  return isAdvanced
    ? createOpenPaydAdvancedModel({ providerTableData, data })
    : createOpenPaydSimpleModel({ providerTableData, data });
}

function buildTemplateRequisites(data) {
  const requisites = {};

  switch (data?.paymentMethod) {
    case PAYMENT_METHOD.SEPA:
    case PAYMENT_METHOD.SEPA_INSTANT: {
      const iban = data?.iban || data?.accountNumber;
      if (iban) requisites.iban = iban;
      break;
    }
    case PAYMENT_METHOD.FASTER_PAYMENTS:
    case PAYMENT_METHOD.BACS:
    case PAYMENT_METHOD.CHAPS: {
      if (data?.iban) {
        requisites.iban = data.iban;
      } else if (data?.accountNumber) {
        requisites.accountNumber = data.accountNumber;
        if (data?.sortCode) requisites.sortCode = data.sortCode;
      }
      break;
    }
    case PAYMENT_METHOD.SWIFT: {
      if (data?.iban) {
        requisites.iban = data.iban;
      } else if (data?.accountNumber) {
        requisites.accountNumber = data.accountNumber;
      }
      if (data?.bankCountry) requisites.bankCountry = data.bankCountry;
      if (data?.bankName) requisites.bankName = data.bankName;
      if (data?.bic) requisites.bic = data.bic;
      break;
    }
    case PAYMENT_METHOD.CRYPTO: {
      const cryptoAddress = data?.cryptoAddress || data?.accountNumber;
      if (cryptoAddress) requisites.cryptoAddress = cryptoAddress;
      const cryptoNetwork = data?.cryptoNetwork || data?.network;
      if (cryptoNetwork) requisites.cryptoNetwork = cryptoNetwork;
      break;
    }
    case PAYMENT_METHOD.INTERNAL: {
      if (data?.accountNumber) requisites.accountNumber = data.accountNumber;
      if (data?.iban) requisites.iban = data.iban;
      break;
    }
    default:
      break;
  }

  return requisites;
}

// Template creation model (original logic)
function createTemplateModel(data) {
  const requestData = {
    newPaymentFormName: data?.newPaymentFormName,
    amount: data?.amount,
    currency: data?.currency,
    paymentMethod: data?.paymentMethod
  };

  // Add paymentPurpose as 'paymentPurpose' for UAE bank country
  if (data?.bankCountry === 'AE' && data?.paymentPurpose) {
    requestData.paymentPurpose = data.paymentPurpose;
  }

  const requisites = buildTemplateRequisites(data);

  const beneficiary = {
    type: data?.type || CUSTOMER_TYPE[data?.recipientType?.toUpperCase()]?.toUpperCase(),
    registrationNumber: data?.registrationNumber,
    city: data?.city,
    country: data?.country,
    postCode: data?.postCode,
    address: data?.address
  };
  if (data?.recipientType === RECIPIENT_TYPES.COMPANY && data?.companyName) beneficiary.companyName = data?.companyName;
  if (data?.recipientType === RECIPIENT_TYPES.INDIVIDUAL && data?.firstName) beneficiary.firstName = data?.firstName;
  if (data?.recipientType === RECIPIENT_TYPES.INDIVIDUAL && data?.lastName) beneficiary.lastName = data?.lastName;

  if (data?.isCryptoPayment) {
    requestData.beneficiary = {
      type: RECIPIENT_TYPES.INDIVIDUAL.toUpperCase(),
      firstName: 'Friendly',
      lastName: 'Recipient'
    };
  } else {
    requestData.comment = data?.transferDetails;
    requestData.beneficiary = beneficiary;
  }

  requestData.requisites = requisites;
  return requestData;
}

// Crypto model
function createCryptoModel({ providerTableData, data }) {
  return {
    paymentMethod: data.paymentMethod || null,
    senderWallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    recipient: {
      walletAddress: providerTableData.recipient_wallet || data.wallet_address,
      network: (data.wallet && data.wallet?.network?.trim()) || ''
    },
    comment: trimTextContent(providerTableData?.comment) || null,
    documents: data.uploadedFiles ? data.uploadedFiles : null
  };
}

// Clear Junction model
function createClearJunctionModel({ providerTableData, data }) {
  return {
    sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    transaction_recipient: {
      beneficiary: {
        type: providerTableData.recipient_type.toUpperCase(),
        ...(providerTableData.recipient_type === 'Company'
          ? {
              company_name: trimTextContent(providerTableData?.company_name) || ''
            }
          : {
              first_name: trimTextContent(providerTableData?.first_name) || '',
              last_name: trimTextContent(providerTableData?.last_name) || ''
            })
      },
      requisites: providerTableData?.recipient_wallet
        ? {
            iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
            bank_country: providerTableData?.bank_country?.trim() || data?.bank_country?.trim()
          }
        : {
            account_number: providerTableData?.account_number?.trim(),
            sort_code: providerTableData?.sort_code?.trim().replace(/-/g, ''),
            bank_country: providerTableData?.bank_country?.trim() || process.env.REACT_APP_DEFAULT_COUNTRY_CODE
          }
    },
    payment_method: data.paymentMethod || '',
    transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
    comment: trimTextContent(providerTableData?.comment) || '',
    documents: data.uploadedFiles ? data.uploadedFiles : []
  };
}

// OpenPayd Simple model
function createOpenPaydSimpleModel({ providerTableData, data }) {
  return {
    sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    transaction_recipient: {
      beneficiary: {
        type: providerTableData.recipient_type.toUpperCase(),
        ...(providerTableData.recipient_type === 'Company'
          ? {
              company_name: trimTextContent(providerTableData?.company_name) || ''
            }
          : {
              first_name: trimTextContent(providerTableData?.first_name) || '',
              last_name: trimTextContent(providerTableData?.last_name) || ''
            })
      },
      requisites:
        providerTableData?.recipient_wallet &&
        data.paymentMethod === PAYMENT_METHOD.INTERNAL &&
        !providerTableData?.sort_code
          ? {
              wallet_number: replaceSpaces(providerTableData?.recipient_wallet) || '',
              bank_country: providerTableData?.bank_country?.trim() || data?.bank_country?.trim()
            }
          : providerTableData?.recipient_wallet &&
              data.paymentMethod !== PAYMENT_METHOD.INTERNAL &&
              !providerTableData?.sort_code
            ? {
                iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
                bank_country: providerTableData?.bank_country?.trim() || data?.bank_country?.trim()
              }
            : {
                account_number: providerTableData?.account_number?.trim(),
                sort_code: providerTableData?.sort_code?.trim().replace(/-/g, ''),
                bank_country: providerTableData?.bank_country?.trim() || process.env.REACT_APP_DEFAULT_COUNTRY_CODE
              }
    },
    payment_method: data.paymentMethod || '',
    transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
    comment: trimTextContent(providerTableData?.comment) || '',
    documents: data.uploadedFiles ? data.uploadedFiles : []
  };
}

// OpenPayd Advanced model (for SWIFT/TARGET2)
function createOpenPaydAdvancedModel({ providerTableData, data }) {
  return {
    sender_wallet: (data.wallet && data.wallet?.wallet_number?.trim()) || '',
    amount: strToFloat(providerTableData?.amount?.toString() || ''),
    currency: (data.wallet && data.wallet?.currency?.trim()) || '',
    transaction_recipient: {
      registration_number: providerTableData.registration_number,
      beneficiary_city: providerTableData.beneficiary_city,
      beneficiary_country: providerTableData.beneficiary_country,
      beneficiary_post_code: providerTableData.beneficiary_post_code,
      ...(isBeneficiaryStateProvider(data.wallet?.transfer_provider) && {
        beneficiary_state: providerTableData.beneficiary_state
      }),
      beneficiary_address: providerTableData.beneficiary_address,
      beneficiary: {
        type: providerTableData.recipient_type.toUpperCase(),
        ...(providerTableData.recipient_type === 'Company'
          ? {
              company_name: trimTextContent(providerTableData?.company_name) || ''
            }
          : {
              first_name: trimTextContent(providerTableData?.first_name) || '',
              last_name: trimTextContent(providerTableData?.last_name) || ''
            })
      },
      requisites:
        data.paymentMethod === PAYMENT_METHOD.SWIFT || data.paymentMethod === PAYMENT_METHOD.TARGET2
          ? {
              // IBAN as resolved on the account step; empty for a Non-IBAN beneficiary (account number only)
              iban: replaceSpaces(data?.iban) || '',
              bank_country: data?.bank_country?.trim() || providerTableData?.bank_country?.trim(),
              bank_name: data?.bank_name?.trim() || providerTableData?.bank_name?.trim(),
              account_number: data?.account_number?.trim() || providerTableData?.account_number?.trim(),
              bic: data?.bic?.trim() || providerTableData?.bic?.trim(),
              // TRN-7466: routing code of a Non-IBAN SWIFT beneficiary (ABA / transit code), from the bank details step.
              // TRN-7602: also with an IBAN when the bank country makes it mandatory (Convera API / Merchant) — the bank
              // details step empties it whenever it doesn't apply.
              ...(data.paymentMethod === PAYMENT_METHOD.SWIFT &&
                data?.routingCode?.trim() && { sort_code: data.routingCode.trim() })
            }
          : {
              iban: replaceSpaces(providerTableData?.recipient_wallet) || '',
              bank_country: data?.bank_country?.trim()
            }
    },
    payment_method: data.paymentMethod || '',
    transfer_provider: (data.wallet && data.wallet?.transfer_provider) || '',
    comment: trimTextContent(providerTableData?.comment) || '',
    documents: data.uploadedFiles ? data.uploadedFiles : []
  };
}
